import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

export interface DashboardStats {
  totalBookings: number;
  totalRevenue: number;
  occupancyRate: number;
  pendingRequests: number;
  upcomingCheckIns: number;
  activeListings: number;
}

export interface UpcomingTrip {
  id: string;
  listingTitle: string;
  checkInDate: string;
  checkOutDate: string;
  guests: number;
  totalPrice: number;
  status: string;
  listingCity: string;
  listingAddress: string;
}

export interface PropertySummary {
  id: string;
  title: string;
  city: string;
  occupancyRate: number;
  revenue: number;
  pendingBookings: number;
  nextCheckIn?: string;
  photos: string[];
  is_active: boolean;
}

export const useDashboardData = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isHost, setIsHost] = useState(false);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats>({
    totalBookings: 0,
    totalRevenue: 0,
    occupancyRate: 0,
    pendingRequests: 0,
    upcomingCheckIns: 0,
    activeListings: 0
  });
  const [upcomingTrips, setUpcomingTrips] = useState<UpcomingTrip[]>([]);
  const [properties, setProperties] = useState<PropertySummary[]>([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Listen to realtime events from GlobalStateContext
  useEffect(() => {
    const handleListingsUpdate = () => {
      console.log('Dashboard: Listings updated, refreshing...');
      setRefreshTrigger(prev => prev + 1);
    };

    window.addEventListener('listings-updated', handleListingsUpdate);

    return () => {
      window.removeEventListener('listings-updated', handleListingsUpdate);
    };
  }, []);

  // Check if user is a host
  useEffect(() => {
    const checkHostStatus = async () => {
      if (!user) return;

      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('user_type, host_approved')
          .eq('user_id', user.id)
          .single();

        const isApprovedHost = profile?.user_type === 'host' && profile?.host_approved;
        setIsHost(isApprovedHost);
      } catch (error) {
        console.error('Error checking host status:', error);
      }
    };

    checkHostStatus();
  }, [user]);

  // Fetch guest data (upcoming trips)
  useEffect(() => {
    const fetchGuestData = async () => {
      if (!user || isHost) return;

      try {
        const { data: bookings } = await supabase
          .from('bookings')
          .select(`
            id,
            check_in_date,
            check_out_date,
            guests_count,
            total_price,
            status,
            listings (
              title,
              city,
              address,
              photos
            )
          `)
          .eq('user_id', user.id)
          .gte('check_in_date', new Date().toISOString())
          .order('check_in_date', { ascending: true })
          .limit(5);

        if (bookings) {
          const trips: UpcomingTrip[] = bookings.map(booking => ({
            id: booking.id,
            listingTitle: (booking.listings as any)?.title || 'Unbekannte Unterkunft',
            checkInDate: booking.check_in_date,
            checkOutDate: booking.check_out_date,
            guests: booking.guests_count,
            totalPrice: Number(booking.total_price),
            status: booking.status,
            listingCity: (booking.listings as any)?.city || 'Unbekannt',
            listingAddress: (booking.listings as any)?.address || ''
          }));

          setUpcomingTrips(trips);
        }
      } catch (error) {
        console.error('Error fetching guest data:', error);
      }
    };

    fetchGuestData();
  }, [user?.id, isHost, refreshTrigger]); // Add refreshTrigger to dependencies

  // Fetch host data (properties and stats)
  useEffect(() => {
    const fetchHostData = async () => {
      if (!user || !isHost) return;

      try {
        // Fetch host's listings
        const { data: listings } = await supabase
          .from('listings')
          .select(`
            id,
            title,
            city,
            photos,
            is_active,
            bookings (
              id,
              check_in_date,
              check_out_date,
              total_price,
              status
            )
          `)
          .eq('user_id', user.id);

        if (listings) {
          let totalBookings = 0;
          let totalRevenue = 0;
          let pendingRequests = 0;
          let upcomingCheckIns = 0;

          const propertySummaries: PropertySummary[] = listings.map(listing => {
            const bookings = (listing.bookings as any[]) || [];
            const confirmedBookings = bookings.filter(b => b.status === 'confirmed');
            const pendingBookings = bookings.filter(b => b.status === 'pending');
            
            const listingRevenue = confirmedBookings.reduce((sum, b) => sum + Number(b.total_price), 0);
            const nextCheckIn = confirmedBookings
              .filter(b => new Date(b.check_in_date) >= new Date())
              .sort((a, b) => new Date(a.check_in_date).getTime() - new Date(b.check_in_date).getTime())[0]?.check_in_date;

            // Calculate occupancy rate (simplified: bookings this month / days in month)
            const thisMonth = new Date();
            const thisMonthBookings = confirmedBookings.filter(b => 
              new Date(b.check_in_date).getMonth() === thisMonth.getMonth() &&
              new Date(b.check_in_date).getFullYear() === thisMonth.getFullYear()
            );
            const occupancyRate = Math.min(100, (thisMonthBookings.length / 30) * 100);

            totalBookings += confirmedBookings.length;
            totalRevenue += listingRevenue;
            pendingRequests += pendingBookings.length;
            if (nextCheckIn && new Date(nextCheckIn) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)) {
              upcomingCheckIns++;
            }

            return {
              id: listing.id,
              title: listing.title,
              city: listing.city,
              occupancyRate,
              revenue: listingRevenue,
              pendingBookings: pendingBookings.length,
              nextCheckIn,
              photos: listing.photos || [],
              is_active: listing.is_active
            };
          });

          setProperties(propertySummaries);
          setDashboardStats({
            totalBookings,
            totalRevenue,
            occupancyRate: listings.length > 0 ? propertySummaries.reduce((sum, p) => sum + p.occupancyRate, 0) / listings.length : 0,
            pendingRequests,
            upcomingCheckIns,
            activeListings: listings.length
          });
        }
      } catch (error) {
        console.error('Error fetching host data:', error);
      }
    };

    fetchHostData();
  }, [user?.id, isHost, refreshTrigger]); // Add refreshTrigger to dependencies

  useEffect(() => {
    if (user !== null) {
      setLoading(false);
    }
  }, [user, isHost]);

  return {
    loading,
    isHost,
    dashboardStats,
    upcomingTrips,
    properties,
    user
  };
};